import { motion } from "framer-motion";
import { CalendarCheck2, BellRing, MessageSquareText, RotateCcw, MoonStar, ArrowUpRight } from "lucide-react";
import { ChapterHeading } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";

const FEATURES = [
  {
    icon: MessageSquareText,
    title: "Atendimento automático",
    text: "Respostas naturais em segundos, com a voz do seu negócio. Sem menus travados, sem robô de filme ruim.",
    span: "md:col-span-2",
    big: true,
  },
  {
    icon: CalendarCheck2,
    title: "Agendamento inteligente",
    text: "Marca, remarca e organiza sua agenda em tempo real, respeitando suas regras e horários.",
  },
  {
    icon: BellRing,
    title: "Confirmação anti-falta",
    text: "Lembretes automáticos que reduzem faltas sem aviso e mantêm a cadeira ocupada.",
  },
  {
    icon: RotateCcw,
    title: "Recuperação de clientes",
    text: "Reativa quem sumiu com abordagens no momento certo — antes que virem clientes de outra pessoa.",
  },
  {
    icon: MoonStar,
    title: "Operação 24h",
    text: "Seu negócio responde mesmo quando as portas estão fechadas. Inclusive domingo de madrugada.",
  },
];

export default function Solution() {
  return (
    <section
      id="solucao"
      data-testid="solution-section"
      className="relative overflow-hidden bg-petrol py-28 text-white sm:py-36"
    >
      <div
        className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, rgba(39,179,176,0.35), transparent 65%)" }}
      />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-10">
        <ChapterHeading
          index="02"
          eyebrow="A solução"
          dark
          lines={[
            "Seu WhatsApp trabalhando",
            "enquanto sua equipe",
            "trabalha.",
          ]}
          sub="Um agente de IA configurado para o seu negócio assume as conversas repetitivas — e devolve tempo, agenda e tranquilidade para a sua equipe."
        />

        <div className="mt-16 grid gap-5 md:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              data-testid={`feature-card-${i + 1}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -5 }}
              className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-sm transition-[border-color,background-color] duration-300 hover:border-turquoise/50 hover:bg-white/[0.07] ${f.span || ""}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-turquoise/15 text-turquoise">
                  <f.icon className="h-6 w-6" />
                </div>
                {f.big && (
                  <span className="rounded-full border border-turquoise/40 px-3 py-1 font-mono2 text-[10px] uppercase tracking-widest text-turquoise">
                    peça central
                  </span>
                )}
              </div>
              <h3 className={`mt-6 font-display font-bold text-white ${f.big ? "text-2xl sm:text-3xl" : "text-xl"}`}>
                {f.title}
              </h3>
              <p className={`mt-3 leading-relaxed text-sage/80 ${f.big ? "max-w-lg text-base" : "text-sm"}`}>
                {f.text}
              </p>
            </motion.div>
          ))}

          <motion.button
            data-testid="solution-cta-demo"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ scale: 1.02 }}
            onClick={() => scrollToId("demonstracao")}
            className="group flex flex-col items-start justify-between rounded-3xl bg-turquoise p-8 text-left text-petrol"
          >
            <ArrowUpRight className="h-8 w-8 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            <div className="mt-10">
              <p className="font-display text-2xl font-extrabold leading-tight">
                Quero isso no meu WhatsApp
              </p>
              <p className="mt-2 text-sm font-medium text-petrol/70">
                Demonstração gratuita, sem compromisso.
              </p>
            </div>
          </motion.button>
        </div>
      </div>
    </section>
  );
}
